'use client';

import { useEffect, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import cx from 'classnames';

import { Image } from '@/components/Default/Image';
import { Link } from '@/components/Default/Link';

import Navigation from './Navigation';

interface SidebarProps {
    open: boolean;
    onClose: () => void;
}

const Sidebar = ({ open, onClose }: SidebarProps) => {
    const controls = useAnimationControls();
    const [currentHref, setHref] = useState<string|null>(null);

    useEffect(() => setHref(window.location.pathname), []);

    useEffect(
        () => {
            if (open) {
                controls.start({ opacity: 1, x: 0 });
            }
            else {
                controls.start({ opacity: 0, x: 200, transition: { delay: 0 } });
            }

            const chatwootApi = window.$chatwoot;

            if (typeof chatwootApi === 'object' && chatwootApi.hasLoaded === true && typeof chatwootApi.toggleBubbleVisibility === 'function') {
                window.$chatwoot.toggleBubbleVisibility(open ? 'hide' : 'show');
            }
        },
        [open, controls]
    );

    return (
        <motion.div
            initial={{
                x: '100%'
            }}
            animate={{
                x: open ? '0%' : '100%'
            }}
            transition={{
                type: 'spring',
                mass: 0.3,
                damping: 13
            }}
            className='fixed w-full sm:w-[640px] h-full right-0 inset-y-0 bg-neutral-950 flex flex-col justify-center tracking overflow-x-hidden overflow-y-auto'
        >
            <motion.div
                className='flex items-center my-8 sm:my-0 sm:mt-0 px-16 sm:px-24'
                initial={{
                    opacity: 0
                }}
                animate={{
                    opacity: open ? 1 : 0
                }}
                transition={{
                    delay: 0.15
                }}
            >
                <div className='relative w-full h-full flex items-center'>
                    <div className='absolute left-0 -translate-x-5 block md:hidden cursor-pointer' onClick={onClose}>
                        <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={1.5} stroke='currentColor' className='w-8 h-8 text-gray-200 hover:text-white'>
                            <path strokeLinecap='round' strokeLinejoin='round' d='M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18' />
                        </svg>
                    </div>
                    <Image src='https://timeforsolar-assets.s3.eu-west-2.amazonaws.com/logo-letterform-06.svg' width={300} height={61.3} alt='' className='-translate-y-[28%] translate-x-5 w-full h-full shrink-0' />
                </div>
            </motion.div>
            <ol className='w-full my-4'>
                {Navigation.map(({ title, href }, idx) => {
                    return (
                        <motion.li
                            key={idx}
                            initial={{
                                opacity: 0,
                                x: 200
                            }}
                            animate={controls}
                            transition={{
                                ease: 'easeOut',
                                delay: 0.45 + (idx * 0.125),
                                duration: 0.25
                            }}
                            className='group relative cursor-pointer border-b last:border-b-0 border-neutral-900 hover:bg-white/5'
                        >
                            <Link href={href} className={cx('w-full h-full tracking-wider font-bold py-4 sm:py-6 px-12 sm:px-24 text-xl sm:text-2xl md:text-3xl whitespace-nowrap', {
                                'text-primary-500': currentHref === href,
                                'text-white/60 group-hover:text-white': currentHref !== href
                            })}>
                                {title}
                            </Link>
                        </motion.li>
                    );
                })}
            </ol>
        </motion.div>
    );
};

export default Sidebar;